import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TicketCard } from "@/components/TicketCard";
import { toast } from "sonner";
import { Mail, Ticket, CheckCircle, AlertCircle, Loader2, Search } from "lucide-react";

export default function TicketRetrievePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Two modes: "request" (email form) OR "view" (?token=... in URL)
  const [mode, setMode] = useState(token ? "view" : "request");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [retrievedEmail, setRetrievedEmail] = useState("");

  useEffect(() => {
    if (token) {
      setMode("view");
      fetchTickets();
    }
  }, [token]);

  const fetchTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/tickets/retrieve`, { params: { token } });
      setTickets(res.data.tickets || []);
      setRetrievedEmail(res.data.email || "");
    } catch (err) {
      setError(err.response?.data?.detail || "We couldn't open this retrieval link. It may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tickets/retrieve/request`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="section-spacing" data-testid="ticket-retrieve-page">
        <div className="container-custom max-w-3xl mx-auto">
          {mode === "request" && !sent && (
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="inline-flex w-16 h-16 rounded-full bg-blue-100 items-center justify-center mb-4">
                  <Ticket className="w-8 h-8 text-deep-blue" />
                </div>
                <h1 className="text-3xl font-bold text-deep-blue mb-2">Retrieve my tickets</h1>
                <p className="text-slate-600">
                  Lost your confirmation email? Enter the email you used at checkout and we'll send you a secure link to all of your tickets.
                </p>
              </div>

              <form onSubmit={handleRequest} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="retrieve-form">
                <div>
                  <Label htmlFor="retrieve-email">Email address</Label>
                  <Input
                    id="retrieve-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    data-testid="retrieve-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={submitting}
                  data-testid="retrieve-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending link...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" /> Send me my tickets</>
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  For your security, tickets are never shown here directly — we always email a one-time link.
                </p>
              </form>
            </div>
          )}

          {mode === "request" && sent && (
            <div className="max-w-md mx-auto text-center py-12" data-testid="retrieve-sent">
              <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-4">
                <Mail className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold text-deep-blue mb-3">Check your email</h1>
              <p className="text-slate-600 mb-4">
                If any tickets are linked to <strong>{email}</strong>, we've sent a retrieval link to that inbox. The link expires in 30 minutes.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Don't see it? Check your spam or promotions folder — it comes from <strong>@bluevsblue.org</strong>.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={() => { setSent(false); setEmail(""); }} data-testid="retrieve-try-again">
                  Try a different email
                </Button>
                <Link to="/events"><Button variant="ghost">Browse events</Button></Link>
              </div>
            </div>
          )}

          {mode === "view" && loading && (
            <div className="text-center py-16">
              <Loader2 className="w-12 h-12 text-deep-blue mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold text-deep-blue">Loading your tickets...</h2>
            </div>
          )}

          {mode === "view" && !loading && error && (
            <div className="text-center py-16" data-testid="retrieve-error">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-deep-blue mb-2">Link expired</h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
              <Link to="/tickets/retrieve">
                <Button className="bg-deep-blue text-white" data-testid="retrieve-request-new">
                  <Mail className="w-4 h-4 mr-2" /> Request a new link
                </Button>
              </Link>
            </div>
          )}

          {mode === "view" && !loading && !error && (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-3xl font-bold text-deep-blue mb-2">Your tickets</h1>
                <p className="text-slate-600">
                  Every ticket linked to <strong>{retrievedEmail}</strong>. Download or print each one — that's your entry pass at the event.
                </p>
              </div>

              {tickets.length === 0 ? (
                <div className="bg-slate-50 rounded-xl p-8 text-center" data-testid="retrieve-empty">
                  <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">No tickets found for this email.</p>
                  <Link to="/events"><Button variant="outline">Browse events</Button></Link>
                </div>
              ) : (
                <div className="grid gap-5" data-testid="retrieve-ticket-list">
                  {tickets.map((t, i) => (
                    <TicketCard key={t.ticket_id} ticket={t} idx={i} total={tickets.length} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
